import BaseController from "niteroi/packages/system/controllers/Base.controller";
import JSONModel from "sap/ui/model/json/JSONModel";
import {ListItemBase$DetailPressEvent} from "sap/m/ListItemBase";
import {Button$PressEvent} from "sap/m/Button";
import Toolbar from "sap/m/Toolbar";
import HeaderContainer from "sap/m/HeaderContainer";
import Page from "sap/m/Page";
import {ILoadDataResponse, IUser} from "niteroi/packages/system/models/interfaces";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";

export default class Roles extends BaseController {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();

        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);

            if (route.getPattern().includes("roles/add")) {
                this.resetOriginalModel("role");
            } else {
                route.detachPatternMatched(this.onPatternMatchedHandler);
                route.attachPatternMatched(this.onPatternMatchedHandler);
            }
        }
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        this.resetOriginalModel("role");
        const params = event.getParameters();
        const id = (params.arguments as { id: string }).id ?? "";
        this.getData(id);
    }

    protected getData(objectId: string = ""): void {
        const model = this.createRequestModel();
        const url = objectId ? `/api/roles/${objectId}` : "/api/roles";
        const modelName = objectId ? "role" : "roles";
        model.loadData(url, "", true, "GET", false, true, this.getRequestHeaders()).then(() => {
            this.originalData = objectId ? Object.assign({}, JSON.parse(model.getJSON())) : {};
            this.setModel(model, modelName);
        });
    }

    public onExit(): void {
        this.isModelSaved = true;
        this.originalData = {};
        const model = this.getModel("role");
        model.destroy();
    }

    public onPressMenuItemAdd(): void {
        this.isModelSaved = true;
        this.originalData = {};
        const model = new JSONModel({isModelSaved: this.isModelSaved}, true);
        this.setModel(model, "role");

        model.attachPropertyChange(this.setModelEditMode.bind(this));

        this.navTo("system/roles/add", {}, false);
    }

    public onPressColumnListItemDetail(event: ListItemBase$DetailPressEvent): void {
        const source = event.getSource();
        const bindingContext = source.getBindingContext("roles");
        const selectedListItem = bindingContext.getModel().getProperty(bindingContext.getPath());
        this.originalData = selectedListItem;
        this.isModelSaved = true;
        selectedListItem.isModelSaved = this.isModelSaved;
        let model = this.getModel("role");
        if (!model) {
            model = new JSONModel({}, true);
            this.setModel(model, "role");
            model.attachPropertyChange(this.setModelEditMode.bind(this));
        }
        model.setData(selectedListItem);
        this.navTo("system/roles/edit", {"id": selectedListItem.roleId}, false);
    }

    public onPressButtonSaveFormEdit(event: Button$PressEvent): void {
        const page = (((event.getSource().getParent() as Toolbar).getParent() as HeaderContainer).getParent() as Page);
        page.setBusy(true);
        const model = this.getModel("role");
        const itemData = model.getData() as IUser;
        let isNew = false;
        if (!itemData.userId) {
            itemData.userId = this.uuidv4();
            isNew = true;
            itemData.companyId = this.getModel("session").getProperty("/company/companyId") as string;
            if (!itemData.password) {
                this.showToast("Password is required");
                page.setBusy(false);
                return;
            }
        }

        if (itemData.firstName && itemData.email) {
            const model = this.createRequestModel();
            const url = "/api/roles";
            const method = isNew ? "POST" : "PUT";
            const headers = this.getRequestHeaders();
            const data = JSON.stringify(itemData);
            model.loadData(url, data, true, method, false, true, headers).then(() => {
                const sessionModel = this.getModel("session");
                if (sessionModel.getProperty("/role/roleId") === itemData.userId) {
                    sessionModel.setProperty("/role", Object.assign({}, itemData));
                }
                this.showToast("Role saved successfully");
            }).catch((response: ILoadDataResponse) => {
                const responseText = JSON.parse(response.responseText);
                this.showToast(`${responseText.code} - ${responseText.error}`);
            }).finally(() => {
                page.setBusy(false);
                this.navTo("system/roles", {}, false);
            })
        }
        page.setBusy(false);
    }

    public onPressButtonDiscardFormEdit(): void {
        this.discardChanges("role");
    }
}
